import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import axios from "./axios";

/**
 * GDPR / RGPD self-service actions for the signed-in user.
 * Backend:
 *   GET    /user/gdpr/export     -> art. 20 portability (JSON snapshot)
 *   DELETE /user/gdpr/anonymize  -> art. 17 erasure (anonymise + soft-delete)
 */

/** Download the caller's personal data as a JSON file (right to portability). */
export const useExportMyData = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.get("/user/gdpr/export");
      return data;
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        t("privacy.exportReady", "Vos données ont été téléchargées")
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};

/** Erase (anonymise) the caller's own account, then log out (right to erasure). */
export const useAnonymizeMyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.delete("/user/gdpr/anonymize");
      return data;
    },
    onSuccess: () => {
      toast.success(t("privacy.accountDeleted", "Votre compte a été supprimé"));
      localStorage.clear();
      navigate("/");
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};
